import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
    LayoutDashboard,
    BookOpen,
    FileText,
    PieChart,
    PlusCircle,
    ArrowLeft,
    CheckCircle,
    XCircle,
    Save,
    Trash2
} from 'lucide-react';

const FinanceDashboard = ({ embedded = false }) => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('adminToken');
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState('dashboard');
    const [groups, setGroups] = useState([]);
    const [ledgers, setLedgers] = useState([]);
    const [loading, setLoading] = useState(false);

    // Forms
    const [newGroup, setNewGroup] = useState({ name: '', nature: 'Assets', parentGroup: '' });
    const [newLedger, setNewLedger] = useState({ name: '', group: '', openingBalance: 0, openingBalanceType: 'Dr' });

    // Voucher
    const [voucher, setVoucher] = useState({
        voucherType: 'Payment',
        voucherNumber: '',
        date: new Date().toISOString().slice(0, 10),
        narration: '',
        entries: [{ ledger: '', type: 'Dr', amount: 0 }]
    });

    // Reports
    const [trialBalance, setTrialBalance] = useState([]);

    useEffect(() => {
        fetchMasters();
        fetchTrialBalance(); // Load stats on mount
    }, []);

    const fetchMasters = async () => {
        setLoading(true);
        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const [groupsRes, ledgersRes] = await Promise.all([
                axios.get(`${API_URL}/api/finance/groups`, config),
                axios.get(`${API_URL}/api/finance/ledgers`, config)
            ]);
            setGroups(groupsRes.data);
            setLedgers(ledgersRes.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateGroup = async (e) => {
        e.preventDefault();
        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            await axios.post(`${API_URL}/api/finance/groups`, newGroup, config);
            fetchMasters();
            setNewGroup({ name: '', nature: 'Assets', parentGroup: '' });
            alert('Group Created');
        } catch (err) {
            alert(err.response?.data?.message || 'Error creating group');
        }
    };

    const handleCreateLedger = async (e) => {
        e.preventDefault();
        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            await axios.post(`${API_URL}/api/finance/ledgers`, newLedger, config);
            fetchMasters();
            setNewLedger({ name: '', group: '', openingBalance: 0, openingBalanceType: 'Dr' });
            alert('Ledger Created');
        } catch (err) {
            alert(err.response?.data?.message || 'Error creating ledger');
        }
    };

    // Voucher Logic
    const addVoucherEntry = () => {
        setVoucher({
            ...voucher,
            entries: [...voucher.entries, { ledger: '', type: 'Dr', amount: 0 }]
        });
    };

    const removeVoucherEntry = (index) => {
        const newEntries = voucher.entries.filter((_, i) => i !== index);
        setVoucher({ ...voucher, entries: newEntries });
    };

    const updateVoucherEntry = (index, field, value) => {
        const newEntries = [...voucher.entries];
        newEntries[index][field] = value;
        setVoucher({ ...voucher, entries: newEntries });
    };

    const calculateTotals = () => {
        let dr = 0, cr = 0;
        voucher.entries.forEach(e => {
            if (e.type === 'Dr') dr += Number(e.amount);
            if (e.type === 'Cr') cr += Number(e.amount);
        });
        return { dr, cr, diff: dr - cr };
    };

    const handleSubmitVoucher = async () => {
        const { diff } = calculateTotals();
        if (Math.abs(diff) > 0.01) {
            alert(`Entries do not match! Difference: ${diff}`);
            return;
        }
        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            await axios.post(`${API_URL}/api/finance/vouchers`, { ...voucher, totalAmount: calculateTotals().dr }, config);
            setVoucher({
                voucherType: 'Payment',
                voucherNumber: '',
                date: new Date().toISOString().slice(0, 10),
                narration: '',
                entries: [{ ledger: '', type: 'Dr', amount: 0 }]
            });
            alert('Voucher Saved Successfully');
        } catch (err) {
            alert(err.response?.data?.message || 'Error saving voucher');
        }
    };

    // Reports Logic
    const fetchTrialBalance = async () => {
        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const res = await axios.get(`${API_URL}/api/finance/reports/trial-balance`, config);
            setTrialBalance(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        if (activeTab === 'reports') fetchTrialBalance();
    }, [activeTab]);

    return (
        <div className={embedded ? "w-full" : "min-h-screen bg-gradient-to-br from-amber-50 via-white to-amber-50"}>
            {/* Header */}
            {!embedded && (
                <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
                    <div className="max-w-7xl mx-auto flex justify-between items-center p-4">
                        <div className="flex items-center gap-3">
                            <button onClick={() => navigate('/admin/dashboard')} className="hover:bg-amber-50 p-2 rounded-lg transition text-amber-600">
                                <ArrowLeft size={20} />
                            </button>
                            <div>
                                <h1 className="text-xl font-bold bg-gradient-to-r from-amber-600 to-amber-500 bg-clip-text text-transparent">Finance ERP</h1>
                                <p className="text-xs text-amber-600/70">Accounting & Bookkeeping</p>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            {['dashboard', 'masters', 'vouchers', 'reports'].map(tab => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`px-4 py-2 rounded-lg text-sm font-semibold capitalize transition ${activeTab === tab
                                        ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-md'
                                        : 'hover:bg-amber-50 text-gray-600'
                                        }`}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {embedded && (
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden mb-6">
                    <div className="p-6 flex flex-col md:flex-row justify-between items-center gap-4 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-gray-100">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-800">Finance ERP</h2>
                            <p className="text-gray-500 text-sm mt-1">Accounting & Bookkeeping</p>
                        </div>
                        <div className="bg-white p-1 rounded-xl flex gap-1 transform transition-all shadow-sm border border-gray-100 overflow-x-auto w-full md:w-auto">
                            {['dashboard', 'masters', 'vouchers', 'reports'].map(tab => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`px-6 py-2 rounded-lg text-sm font-bold capitalize transition-all duration-200 whitespace-nowrap ${activeTab === tab
                                        ? 'bg-amber-500 text-white shadow-md scale-105'
                                        : 'text-gray-600 hover:bg-gray-50'
                                        }`}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <div className={embedded ? "w-full" : "max-w-7xl mx-auto p-6"}>
                {/* DASHBOARD TAB */}
                {activeTab === 'dashboard' && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <div className="bg-white p-6 rounded-2xl shadow-lg border border-amber-100 hover:shadow-xl transition-shadow">
                            <h3 className="text-gray-500 font-semibold mb-2 flex items-center gap-2">
                                <BookOpen size={18} className="text-amber-500" /> Total Ledgers
                            </h3>
                            <p className="text-3xl font-bold text-gray-800">{ledgers.length}</p>
                        </div>
                        <div className="bg-white p-6 rounded-2xl shadow-lg border border-amber-100 hover:shadow-xl transition-shadow">
                            <h3 className="text-gray-500 font-semibold mb-2 flex items-center gap-2">
                                <FileText size={18} className="text-amber-500" /> Account Groups
                            </h3>
                            <p className="text-3xl font-bold text-gray-800">{groups.length}</p>
                        </div>
                        <div className="bg-white p-6 rounded-2xl shadow-lg border border-green-100 hover:shadow-xl transition-shadow">
                            <h3 className="text-green-600 font-semibold mb-2 flex items-center gap-2">
                                <PieChart size={18} /> Total Revenue
                            </h3>
                            <p className="text-3xl font-bold text-green-700">
                                ₹{trialBalance
                                    .filter(r => r.group === 'Sales Accounts' && r.closingType === 'Cr')
                                    .reduce((sum, r) => sum + r.closingBalance, 0)
                                    .toFixed(2)}
                            </p>
                        </div>
                        <div className="bg-white p-6 rounded-2xl shadow-lg border border-blue-100 hover:shadow-xl transition-shadow">
                            <h3 className="text-blue-600 font-semibold mb-2 flex items-center gap-2">
                                <LayoutDashboard size={18} /> Cash in Hand
                            </h3>
                            <p className="text-3xl font-bold text-blue-700">
                                ₹{trialBalance
                                    .filter(r => r.group === 'Cash-in-Hand' && r.closingType === 'Dr')
                                    .reduce((sum, r) => sum + r.closingBalance, 0)
                                    .toFixed(2)}
                            </p>
                        </div>
                        <div className="bg-gradient-to-br from-amber-500 to-amber-600 p-6 rounded-2xl shadow-lg text-white md:col-span-4">
                            <h3 className="text-amber-50 font-semibold mb-2">Quick Action</h3>
                            <button
                                onClick={() => setActiveTab('vouchers')}
                                className="bg-white text-amber-600 px-4 py-2 rounded-lg font-bold w-full mt-2 hover:bg-amber-50 transition shadow-sm"
                            >
                                New Voucher
                            </button>
                        </div>
                    </div>
                )}

                {/* MASTERS TAB */}
                {activeTab === 'masters' && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Create Group */}
                        <div className="bg-white p-6 rounded-2xl shadow-lg border border-amber-100">
                            <h2 className="text-lg font-bold mb-4 flex items-center gap-2 text-gray-800">
                                <PlusCircle className="text-amber-600" /> Create Account Group
                            </h2>
                            <form onSubmit={handleCreateGroup} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Group Name</label>
                                    <input
                                        className="w-full border-2 border-gray-100 p-2 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all"
                                        value={newGroup.name}
                                        onChange={e => setNewGroup({ ...newGroup, name: e.target.value })}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Nature</label>
                                    <select
                                        className="w-full border-2 border-gray-100 p-2 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all"
                                        value={newGroup.nature}
                                        onChange={e => setNewGroup({ ...newGroup, nature: e.target.value })}
                                    >
                                        <option>Assets</option>
                                        <option>Liabilities</option>
                                        <option>Income</option>
                                        <option>Expenses</option>
                                    </select>
                                </div>
                                <button className="w-full bg-gray-800 text-white py-3 rounded-xl hover:bg-gray-900 transition-colors font-semibold">Create Group</button>
                            </form>

                            <div className="mt-6 border-t border-gray-100 pt-4">
                                <div className="flex justify-between items-center mb-2">
                                    <h3 className="font-bold text-sm text-gray-600">Existing Groups</h3>
                                    <button
                                        onClick={async () => {
                                            try {
                                                const config = { headers: { Authorization: `Bearer ${token}` } };
                                                await axios.post(`${API_URL}/api/finance/groups/init`, {}, config);
                                                fetchMasters();
                                                alert('Default Groups Initialized');
                                            } catch (err) {
                                                alert('Error initializing groups');
                                            }
                                        }}
                                        className="text-xs bg-amber-100 text-amber-700 hover:bg-amber-200 px-3 py-1 rounded-lg font-semibold transition-colors"
                                    >
                                        Init Defaults
                                    </button>
                                </div>
                                <div className="h-40 overflow-y-auto space-y-1 pr-1">
                                    {groups.map(g => (
                                        <div key={g._id} className="text-sm bg-gray-50 p-3 rounded-lg flex justify-between border border-gray-100">
                                            <span className="font-medium text-gray-800">{g.name}</span>
                                            <span className="text-amber-600 text-xs font-semibold bg-amber-50 px-2 py-0.5 rounded-full">{g.nature}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Create Ledger */}
                        <div className="bg-white p-6 rounded-2xl shadow-lg border border-amber-100">
                            <h2 className="text-lg font-bold mb-4 flex items-center gap-2 text-gray-800">
                                <BookOpen className="text-green-600" /> Create Ledger
                            </h2>
                            <form onSubmit={handleCreateLedger} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Ledger Name</label>
                                    <input
                                        className="w-full border-2 border-gray-100 p-2 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all"
                                        value={newLedger.name}
                                        onChange={e => setNewLedger({ ...newLedger, name: e.target.value })}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Under Group</label>
                                    <select
                                        className="w-full border-2 border-gray-100 p-2 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all"
                                        value={newLedger.group}
                                        onChange={e => setNewLedger({ ...newLedger, group: e.target.value })}
                                        required
                                    >
                                        <option value="">Select Group</option>
                                        {groups.map(g => <option key={g._id} value={g._id}>{g.name}</option>)}
                                    </select>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Op. Balance</label>
                                        <input
                                            type="number"
                                            className="w-full border-2 border-gray-100 p-2 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all"
                                            value={newLedger.openingBalance}
                                            onChange={e => setNewLedger({ ...newLedger, openingBalance: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                                        <select
                                            className="w-full border-2 border-gray-100 p-2 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all"
                                            value={newLedger.openingBalanceType}
                                            onChange={e => setNewLedger({ ...newLedger, openingBalanceType: e.target.value })}
                                        >
                                            <option>Dr</option>
                                            <option>Cr</option>
                                        </select>
                                    </div>
                                </div>
                                <button className="w-full bg-green-600 text-white py-3 rounded-xl hover:bg-green-700 transition-colors font-semibold">Create Ledger</button>
                            </form>

                            <div className="mt-6 border-t border-gray-100 pt-4">
                                <h3 className="font-bold text-sm mb-2 text-gray-600">Recent Ledgers</h3>
                                <div className="h-40 overflow-y-auto space-y-1 pr-1">
                                    {ledgers.map(l => (
                                        <div key={l._id} className="text-sm bg-gray-50 p-3 rounded-lg flex justify-between border border-gray-100">
                                            <span className="font-medium text-gray-800">{l.name}</span>
                                            <span className="text-gray-500 text-xs bg-white px-2 py-0.5 rounded border border-gray-200">{l.group?.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* VOUCHERS TAB */}
                {activeTab === 'vouchers' && (
                    <div className="bg-white p-6 rounded-2xl shadow-lg border border-amber-100">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold text-gray-800">Voucher Entry</h2>
                            <div className="bg-amber-50 text-amber-700 px-4 py-2 rounded-lg font-mono text-sm border border-amber-100 font-semibold">
                                {voucher.date} | #{voucher.voucherNumber || 'AUTO'}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Voucher Type</label>
                                <select
                                    className="w-full border-2 border-gray-100 p-2 rounded-xl bg-gray-50 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all"
                                    value={voucher.voucherType}
                                    onChange={e => setVoucher({ ...voucher, voucherType: e.target.value })}
                                >
                                    {['Contra', 'Payment', 'Receipt', 'Journal', 'Sales', 'Purchase'].map(t => (
                                        <option key={t}>{t}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Voucher No.</label>
                                <input
                                    className="w-full border-2 border-gray-100 p-2 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all"
                                    placeholder="Optional / Auto"
                                    value={voucher.voucherNumber}
                                    onChange={e => setVoucher({ ...voucher, voucherNumber: e.target.value })}
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                                <input
                                    type="date"
                                    className="w-full border-2 border-gray-100 p-2 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all"
                                    value={voucher.date}
                                    onChange={e => setVoucher({ ...voucher, date: e.target.value })}
                                />
                            </div>
                        </div>

                        {/* Entries Table */}
                        <div className="border border-gray-200 rounded-xl overflow-hidden mb-6">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 text-gray-700">
                                    <tr>
                                        <th className="p-3 text-left w-20 font-bold border-b border-gray-200">Type</th>
                                        <th className="p-3 text-left font-bold border-b border-gray-200">Particulars (Ledger)</th>
                                        <th className="p-3 text-right w-32 font-bold border-b border-gray-200">Debit</th>
                                        <th className="p-3 text-right w-32 font-bold border-b border-gray-200">Credit</th>
                                        <th className="p-3 text-center w-16 font-bold border-b border-gray-200">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {voucher.entries.map((entry, idx) => (
                                        <tr key={idx} className="bg-white hover:bg-amber-50/50 transition-colors">
                                            <td className="p-2">
                                                <select
                                                    className="w-full border-none bg-transparent focus:ring-0 font-bold text-gray-700"
                                                    value={entry.type}
                                                    onChange={e => updateVoucherEntry(idx, 'type', e.target.value)}
                                                >
                                                    <option>Dr</option>
                                                    <option>Cr</option>
                                                </select>
                                            </td>
                                            <td className="p-2">
                                                <select
                                                    className="w-full border border-gray-200 p-1.5 rounded-lg focus:border-amber-500 outline-none"
                                                    value={entry.ledger}
                                                    onChange={e => updateVoucherEntry(idx, 'ledger', e.target.value)}
                                                >
                                                    <option value="">Select Ledger</option>
                                                    {ledgers.map(l => <option key={l._id} value={l._id}>{l.name}</option>)}
                                                </select>
                                                <div className="text-xs text-amber-600/70 mt-1 pl-1 font-medium">
                                                    Cur Bal: {ledgers.find(l => l._id === entry.ledger)?.currentBalance || 0}
                                                </div>
                                            </td>
                                            <td className="p-2 text-right">
                                                {entry.type === 'Dr' ? (
                                                    <input
                                                        type="number"
                                                        className="w-full text-right border border-gray-200 p-1.5 rounded-lg focus:border-amber-500 outline-none"
                                                        value={entry.amount}
                                                        onChange={e => updateVoucherEntry(idx, 'amount', e.target.value)}
                                                    />
                                                ) : <span className="text-gray-300">-</span>}
                                            </td>
                                            <td className="p-2 text-right">
                                                {entry.type === 'Cr' ? (
                                                    <input
                                                        type="number"
                                                        className="w-full text-right border border-gray-200 p-1.5 rounded-lg focus:border-amber-500 outline-none"
                                                        value={entry.amount}
                                                        onChange={e => updateVoucherEntry(idx, 'amount', e.target.value)}
                                                    />
                                                ) : <span className="text-gray-300">-</span>}
                                            </td>
                                            <td className="p-2 text-center">
                                                <button onClick={() => removeVoucherEntry(idx)} className="text-red-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded-lg transition-colors">
                                                    <Trash2 size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="bg-gray-50 font-bold text-gray-800 border-t border-gray-200">
                                    <tr>
                                        <td colSpan={2} className="p-3 text-right">Totals:</td>
                                        <td className="p-3 text-right text-amber-700">{calculateTotals().dr.toFixed(2)}</td>
                                        <td className="p-3 text-right text-amber-700">{calculateTotals().cr.toFixed(2)}</td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        <div className="flex justify-between items-center">
                            <button onClick={addVoucherEntry} className="text-amber-600 font-bold hover:text-amber-700 hover:underline text-sm flex items-center gap-1">
                                <PlusCircle size={16} /> Add Another Entry
                            </button>
                            <div className="flex items-center gap-4">
                                <span className={`text-sm font-bold bg-white px-3 py-1 rounded-lg border ${Math.abs(calculateTotals().diff) < 0.01 ? 'text-green-600 border-green-200' : 'text-red-600 border-red-200'}`}>
                                    Difference: {calculateTotals().diff.toFixed(2)}
                                </span>
                                <button
                                    onClick={handleSubmitVoucher}
                                    disabled={Math.abs(calculateTotals().diff) > 0.01}
                                    className="bg-gradient-to-r from-amber-600 to-amber-500 text-white px-6 py-2.5 rounded-xl font-bold hover:shadow-lg hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none transition-all flex items-center gap-2"
                                >
                                    <Save size={18} /> Save Voucher
                                </button>
                            </div>
                        </div>

                        <div className="mt-6">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Narration</label>
                            <textarea
                                className="w-full border-2 border-gray-100 p-3 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all"
                                rows="2"
                                placeholder="Enter transaction details..."
                                value={voucher.narration}
                                onChange={e => setVoucher({ ...voucher, narration: e.target.value })}
                            ></textarea>
                        </div>
                    </div>
                )}

                {/* REPORTS TAB */}
                {activeTab === 'reports' && (
                    <div className="bg-white p-6 rounded-2xl shadow-lg border border-amber-100">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold text-gray-800">Trial Balance</h2>
                            <button onClick={fetchTrialBalance} className="text-amber-600 hover:text-amber-800 hover:underline text-sm font-semibold">Refresh Report</button>
                        </div>

                        <div className="overflow-x-auto rounded-xl border border-gray-200">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-amber-500 text-white uppercase font-bold text-xs tracking-wider">
                                    <tr>
                                        <th className="px-6 py-4">Ledger Name</th>
                                        <th className="px-6 py-4">Group</th>
                                        <th className="px-6 py-4 text-right">Debit Balance</th>
                                        <th className="px-6 py-4 text-right">Credit Balance</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {trialBalance.map((row, idx) => (
                                        <tr key={idx} className="hover:bg-amber-50 transition-colors">
                                            <td className="px-6 py-4 font-semibold text-gray-900 border-r border-gray-50">{row.ledger}</td>
                                            <td className="px-6 py-4 text-gray-500 border-r border-gray-50">{row.group}</td>
                                            <td className="px-6 py-4 text-right font-mono text-gray-700 bg-gray-50/50">
                                                {row.closingType === 'Dr' ? `₹${row.closingBalance.toFixed(2)}` : '-'}
                                            </td>
                                            <td className="px-6 py-4 text-right font-mono text-gray-700">
                                                {row.closingType === 'Cr' ? `₹${row.closingBalance.toFixed(2)}` : '-'}
                                            </td>
                                        </tr>
                                    ))}
                                    <tr className="bg-gray-50 font-bold border-t-2 border-amber-200">
                                        <td className="px-6 py-4 text-gray-800" colSpan={2}>Total</td>
                                        <td className="px-6 py-4 text-right text-amber-700 border-t-2 border-amber-400">
                                            ₹{trialBalance.reduce((sum, row) => sum + (row.closingType === 'Dr' ? row.closingBalance : 0), 0).toFixed(2)}
                                        </td>
                                        <td className="px-6 py-4 text-right text-amber-700 border-t-2 border-amber-400">
                                            ₹{trialBalance.reduce((sum, row) => sum + (row.closingType === 'Cr' ? row.closingBalance : 0), 0).toFixed(2)}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FinanceDashboard;
